import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CashFlowChart = ({ organizationId, refreshTrigger }) => {
  const { userProfile, isAuthenticated } = useAuth();
  const [timeRange, setTimeRange] = useState('6months');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [projectionData, setProjectionData] = useState([]);
  const [customizeSettings, setCustomizeSettings] = useState({
    showProjections: true,
    timeRange: '6months',
    chartType: 'area',
    showMetrics: true,
    colorScheme: 'default',
    showLegend: true,
    showGrid: true,
    animationSpeed: 'normal'
  });

  useEffect(() => {
    if (isAuthenticated && (organizationId || userProfile?.organization_id)) {
      loadCashFlowData();
    }
  }, [isAuthenticated, organizationId, userProfile?.organization_id, refreshTrigger, timeRange]);

  const loadCashFlowData = async () => {
    const orgId = organizationId || userProfile?.organization_id;
    if (!orgId) return;

    try {
      setLoading(true);
      setError(null);

      // Calculate date range based on selection
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case '3months':
          startDate?.setMonth(endDate?.getMonth() - 3);
          break;
        case '12months':
          startDate?.setMonth(endDate?.getMonth() - 12);
          break;
        case 'ytd':
          startDate?.setMonth(0, 1); // Start of year
          break;
        default: // 6months
          startDate?.setMonth(endDate?.getMonth() - 6);
      }

      // Fetch cash inflows (payments received and paid invoices)
      const { data: payments, error: paymentsError } = await supabase
        ?.from('payments_received')
        ?.select('amount_received, payment_date, status')
        ?.eq('organization_id', orgId)
        ?.eq('status', 'cleared')
        ?.gte('payment_date', startDate?.toISOString()?.split('T')?.[0])
        ?.lte('payment_date', endDate?.toISOString()?.split('T')?.[0]);

      // Fetch cash outflows (paid bills)
      const { data: bills, error: billsError } = await supabase
        ?.from('bills')
        ?.select('total_amount, issue_date, status, amount_paid')
        ?.eq('organization_id', orgId)
        ?.in('status', ['paid', 'approved'])
        ?.gte('issue_date', startDate?.toISOString()?.split('T')?.[0])
        ?.lte('issue_date', endDate?.toISOString()?.split('T')?.[0]);

      // Fetch journal entries for additional cash flow data
      const { data: journalEntries, error: journalError } = await supabase
        ?.from('journal_entries')
        ?.select(`
          entry_date,
          total_debit,
          total_credit,
          status,
          journal_entry_lines (
            debit_amount,
            credit_amount,
            chart_of_accounts (
              account_type,
              account_subtype,
              is_cash_account,
              is_bank_account
            )
          )
        `)
        ?.eq('organization_id', orgId)
        ?.eq('status', 'posted')
        ?.gte('entry_date', startDate?.toISOString()?.split('T')?.[0])
        ?.lte('entry_date', endDate?.toISOString()?.split('T')?.[0]);

      if (paymentsError || billsError || journalError) {
        console.error('Error loading cash flow data:', { paymentsError, billsError, journalError });
        setError('Failed to load cash flow data');
        return;
      }

      // Process data by month
      const monthlyData = processMonthlyData(payments, bills, journalEntries, startDate, endDate);
      setCashFlowData(monthlyData);

      // Generate AI-powered projections based on historical data
      const projections = generateProjections(monthlyData);
      setProjectionData(projections);

    } catch (err) {
      console.error('Error loading cash flow data:', err);
      setError('Failed to load cash flow data');
    } finally {
      setLoading(false);
    }
  };

  const processMonthlyData = (payments, bills, journalEntries, startDate, endDate) => {
    const monthlyMap = new Map();
    
    // Initialize months
    const current = new Date(startDate);
    while (current <= endDate) {
      const monthKey = `${current?.getFullYear()}-${String(current?.getMonth() + 1)?.padStart(2, '0')}`;
      const monthName = current?.toLocaleDateString('en-US', { month: 'short' });
      
      monthlyMap?.set(monthKey, {
        month: monthName,
        year: current?.getFullYear(),
        inflow: 0,
        outflow: 0,
        net: 0
      });
      
      current?.setMonth(current?.getMonth() + 1);
    }

    // Process payments (cash inflows)
    payments?.forEach(payment => {
      const date = new Date(payment?.payment_date);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (monthlyMap?.has(monthKey)) {
        const monthData = monthlyMap?.get(monthKey);
        monthData.inflow += Number(payment?.amount_received || 0);
        monthlyMap?.set(monthKey, monthData);
      }
    });

    // Process bills (cash outflows)
    bills?.forEach(bill => {
      const date = new Date(bill?.issue_date);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (monthlyMap?.has(monthKey)) {
        const monthData = monthlyMap?.get(monthKey);
        monthData.outflow += Number(bill?.amount_paid || bill?.total_amount || 0);
        monthlyMap?.set(monthKey, monthData);
      }
    });

    // Process journal entries for cash accounts
    journalEntries?.forEach(entry => {
      const date = new Date(entry?.entry_date);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (monthlyMap?.has(monthKey)) {
        const monthData = monthlyMap?.get(monthKey);
        
        entry?.journal_entry_lines?.forEach(line => {
          const account = line?.chart_of_accounts;
          if (account?.is_cash_account || account?.is_bank_account) {
            // Cash and bank accounts: debits are inflows, credits are outflows
            if (line?.debit_amount > 0) {
              monthData.inflow += Number(line?.debit_amount);
            }
            if (line?.credit_amount > 0) {
              monthData.outflow += Number(line?.credit_amount);
            }
          }
        });
        
        monthlyMap?.set(monthKey, monthData);
      }
    });

    // Calculate net flow and convert to array
    return Array?.from(monthlyMap?.values())?.map(data => ({
      ...data,
      net: data?.inflow - data?.outflow
    }));
  };

  const generateProjections = (historicalData) => {
    if (!historicalData?.length || historicalData?.length < 3) {
      // Return empty projections when insufficient data - no more dummy data
      return [];
    }

    // Calculate trends
    const recentMonths = historicalData?.slice(-3); // Last 3 months
    const avgInflow = recentMonths?.reduce((sum, month) => sum + month?.inflow, 0) / recentMonths?.length;
    const avgOutflow = recentMonths?.reduce((sum, month) => sum + month?.outflow, 0) / recentMonths?.length;
    
    // Calculate growth rates
    const inflowGrowthRate = calculateGrowthRate(recentMonths?.map(m => m?.inflow));
    const outflowGrowthRate = calculateGrowthRate(recentMonths?.map(m => m?.outflow));

    // Generate next 3 months projections
    const projections = [];
    const currentDate = new Date();
    
    for (let i = 1; i <= 3; i++) {
      const projectedDate = new Date(currentDate);
      projectedDate?.setMonth(currentDate?.getMonth() + i);
      
      const projectedInflow = Math.max(0, avgInflow * (1 + inflowGrowthRate * i));
      const projectedOutflow = Math.max(0, avgOutflow * (1 + outflowGrowthRate * i));
      
      projections?.push({
        month: projectedDate?.toLocaleDateString('en-US', { month: 'short' }),
        inflow: Math.round(projectedInflow),
        outflow: Math.round(projectedOutflow),
        net: Math.round(projectedInflow - projectedOutflow),
        projected: true
      });
    }

    return projections;
  };

  const calculateGrowthRate = (values) => {
    if (values?.length < 2) return 0;
    
    let totalGrowth = 0;
    let validGrowthCount = 0;
    
    for (let i = 1; i < values?.length; i++) {
      if (values?.[i - 1] > 0) {
        totalGrowth += (values?.[i] - values?.[i - 1]) / values?.[i - 1];
        validGrowthCount++;
      }
    }
    
    return validGrowthCount > 0 ? totalGrowth / validGrowthCount : 0;
  };

  const allData = customizeSettings?.showProjections ? [...cashFlowData, ...projectionData] : cashFlowData;

  // Download functionality
  const handleCashFlowDownload = async (format) => {
    setDownloadLoading(true);
    
    try {
      if (format === 'pdf') {
        await generateCashFlowPDFReport();
      } else if (format === 'xls') {
        await generateCashFlowXLSReport();
      }
    } catch (error) {
      console.error('Cash Flow download error:', error);
      alert(`Failed to download ${format?.toUpperCase()} report. Please try again.`);
    } finally {
      setDownloadLoading(false);
      setShowDownloadModal(false);
    }
  };

  const generateCashFlowPDFReport = async () => {
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    
    const doc = new jsPDF();
    
    // Header
    doc?.setFontSize(20);
    doc?.text('Cash Flow Analysis Report', 20, 20);
    
    doc?.setFontSize(12);
    doc?.text(`Generated: ${new Date()?.toLocaleString()}`, 20, 35);
    doc?.text(`Period: ${customizeSettings?.timeRange} (Live Data)`, 20, 45);
    doc?.text(`Organization: ${userProfile?.organization_name || 'Current Organization'}`, 20, 55);
    
    // Cash Flow Data Table
    const cashFlowTableData = allData?.map(item => [
      item?.month,
      `$${item?.inflow?.toLocaleString()}`,
      `$${item?.outflow?.toLocaleString()}`,
      `$${item?.net?.toLocaleString()}`,
      item?.projected ? 'Projected' : 'Actual'
    ]);
    
    autoTable(doc, {
      head: [['Month', 'Cash Inflow', 'Cash Outflow', 'Net Flow', 'Type']],
      body: cashFlowTableData,
      startY: 70,
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 10 }
    });
    
    // Summary metrics
    const actualData = cashFlowData?.filter(item => !item?.projected);
    const totalNetFlow = actualData?.reduce((sum, item) => sum + item?.net, 0);
    const avgMonthlyFlow = actualData?.length ? totalNetFlow / actualData?.length : 0;
    const bestMonth = actualData?.reduce((best, current) => 
      current?.net > (best?.net || 0) ? current : best, {});
    
    const summaryData = [
      ['Total Net Cash Flow', `$${totalNetFlow?.toLocaleString()}`],
      ['Average Monthly Flow', `$${Math.round(avgMonthlyFlow)?.toLocaleString()}`],
      ['Best Performing Month', `${bestMonth?.month || 'N/A'} ($${bestMonth?.net?.toLocaleString() || '0'})`],
      ['Data Source', 'Live Supabase Database'],
      ['Projection Method', 'AI-powered trend analysis']
    ];
    
    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: summaryData,
      startY: doc?.lastAutoTable?.finalY + 20,
      headStyles: { fillColor: [34, 197, 94] },
      styles: { fontSize: 10 }
    });
    
    // Footer
    doc?.setFontSize(8);
    doc?.text('Generated by AccounTech AI - Live Cash Flow Analysis', 20, doc?.internal?.pageSize?.height - 20);
    
    doc?.save(`CashFlow_Analysis_Live_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
  };

  const generateCashFlowXLSReport = async () => {
    const XLSX = await import('xlsx');
    
    const wb = XLSX?.utils?.book_new();
    
    // Cash Flow Data Sheet
    const cashFlowSheetData = [
      ['Live Cash Flow Analysis Report'],
      [`Generated: ${new Date()?.toLocaleString()}`],
      [`Period: ${customizeSettings?.timeRange}`],
      [`Organization: ${userProfile?.organization_name || 'Current Organization'}`],
      [''],
      ['Month', 'Cash Inflow', 'Cash Outflow', 'Net Cash Flow', 'Type'],
      ...allData?.map(item => [
        item?.month,
        item?.inflow,
        item?.outflow,
        item?.net,
        item?.projected ? 'Projected' : 'Actual'
      ])
    ];
    
    const cashFlowWS = XLSX?.utils?.aoa_to_sheet(cashFlowSheetData);
    XLSX?.utils?.book_append_sheet(wb, cashFlowWS, 'Cash Flow');
    
    // Summary Sheet
    const actualData = cashFlowData?.filter(item => !item?.projected);
    const totalNetFlow = actualData?.reduce((sum, item) => sum + item?.net, 0);
    const avgMonthlyFlow = actualData?.length ? totalNetFlow / actualData?.length : 0;
    const bestMonth = actualData?.reduce((best, current) => 
      current?.net > (best?.net || 0) ? current : best, {});
    
    const summaryData = [
      ['Live Cash Flow Summary'],
      [''],
      ['Metric', 'Value'],
      ['Total Net Cash Flow', totalNetFlow],
      ['Average Monthly Flow', Math.round(avgMonthlyFlow)],
      ['Number of Actual Months', actualData?.length],
      ['Projection Months', projectionData?.length],
      ['Best Performing Month', bestMonth?.month || 'N/A'],
      ['Best Performance Value', bestMonth?.net || 0],
      ['Data Source', 'Live Supabase Database'],
      ['Last Updated', new Date()?.toLocaleString()],
      [''],
      ['Data Sources Used:'],
      ['- Payments Received (cleared status)'],
      ['- Bills (paid/approved status)'],
      ['- Journal Entries (posted status)'],
      ['- Cash/Bank Account Transactions']
    ];
    
    const summaryWS = XLSX?.utils?.aoa_to_sheet(summaryData);
    XLSX?.utils?.book_append_sheet(wb, summaryWS, 'Summary');
    
    XLSX?.writeFile(wb, `CashFlow_Analysis_Live_${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
  };

  const handleCustomizeApply = () => {
    setShowCustomizeModal(false);
    if (customizeSettings?.timeRange !== timeRange) {
      setTimeRange(customizeSettings?.timeRange);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-elevated">
          <div className="flex items-center space-x-2 mb-3">
            <p className="font-medium text-foreground">{label} {data?.year || new Date()?.getFullYear()}</p>
            {data?.projected && (
              <span className="px-2 py-1 bg-warning/20 text-warning text-xs rounded-full font-medium">
                AI Projected
              </span>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-success rounded-full" />
                <span className="text-sm text-muted-foreground">Cash Inflow:</span>
              </div>
              <span className="text-sm font-medium text-foreground">
                ${data?.inflow?.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-error rounded-full" />
                <span className="text-sm text-muted-foreground">Cash Outflow:</span>
              </div>
              <span className="text-sm font-medium text-foreground">
                ${data?.outflow?.toLocaleString()}
              </span>
            </div>
            <div className="pt-2 border-t border-border">
              <div className="flex items-center justify-between space-x-4">
                <span className="text-sm font-medium text-foreground">Net Cash Flow:</span>
                <span className={`text-sm font-semibold ${data?.net >= 0 ? 'text-success' : 'text-error'}`}>
                  ${data?.net?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const actualData = cashFlowData?.filter(item => !item?.projected);
  const totalNetFlow = actualData?.reduce((sum, item) => sum + item?.net, 0);
  const avgMonthlyFlow = actualData?.length ? totalNetFlow / actualData?.length : 0;

  const timeRangeOptions = [
    { value: '3months', label: 'Last 3 Months' },
    { value: '6months', label: 'Last 6 Months' },
    { value: '12months', label: 'Last 12 Months' },
    { value: 'ytd', label: 'Year to Date' }
  ];

  const chartTypeOptions = [
    { value: 'area', label: 'Area Chart' },
    { value: 'line', label: 'Line Chart' },
    { value: 'bar', label: 'Bar Chart' }
  ];

  const colorSchemeOptions = [
    { value: 'default', label: 'Default Colors' },
    { value: 'cool', label: 'Cool Tones' },
    { value: 'warm', label: 'Warm Tones' },
    { value: 'monochrome', label: 'Monochrome' }
  ];

  const animationOptions = [
    { value: 'none', label: 'No Animation' },
    { value: 'slow', label: 'Slow (2s)' },
    { value: 'normal', label: 'Normal (1s)' },
    { value: 'fast', label: 'Fast (0.5s)' }
  ];

  // Customize Modal
  const renderCustomizeModal = () => {
    if (!showCustomizeModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-2xl max-h-[80vh] overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-foreground flex items-center">
                  <Icon name="Settings" size={24} className="mr-3" />
                  Customize Cash Flow Analysis
                </h3>
                <p className="text-muted-foreground mt-1">
                  Configure chart display, data range, and visual preferences for live data
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowCustomizeModal(false)}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          </div>
          
          <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Data Settings */}
              <div className="space-y-4">
                <h4 className="font-semibold text-foreground flex items-center">
                  <Icon name="Database" size={18} className="mr-2" />
                  Data Settings
                </h4>
                
                <Select
                  label="Time Range"
                  options={timeRangeOptions}
                  value={customizeSettings?.timeRange}
                  onChange={(value) => setCustomizeSettings({...customizeSettings, timeRange: value})}
                />
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showProjections"
                    checked={customizeSettings?.showProjections}
                    onChange={(e) => setCustomizeSettings({...customizeSettings, showProjections: e?.target?.checked})}
                    className="rounded border border-border"
                  />
                  <label htmlFor="showProjections" className="text-sm font-medium text-foreground">
                    Show AI Projections
                  </label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showMetrics"
                    checked={customizeSettings?.showMetrics}
                    onChange={(e) => setCustomizeSettings({...customizeSettings, showMetrics: e?.target?.checked})}
                    className="rounded border border-border"
                  />
                  <label htmlFor="showMetrics" className="text-sm font-medium text-foreground">
                    Show Summary Metrics
                  </label>
                </div>
              </div>

              {/* Visual Settings */}
              <div className="space-y-4">
                <h4 className="font-semibold text-foreground flex items-center">
                  <Icon name="Palette" size={18} className="mr-2" />
                  Visual Settings
                </h4>
                
                <Select
                  label="Chart Type"
                  options={chartTypeOptions}
                  value={customizeSettings?.chartType}
                  onChange={(value) => setCustomizeSettings({...customizeSettings, chartType: value})}
                />
                
                <Select
                  label="Color Scheme"
                  options={colorSchemeOptions}
                  value={customizeSettings?.colorScheme}
                  onChange={(value) => setCustomizeSettings({...customizeSettings, colorScheme: value})}
                />
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showLegend"
                    checked={customizeSettings?.showLegend}
                    onChange={(e) => setCustomizeSettings({...customizeSettings, showLegend: e?.target?.checked})}
                    className="rounded border border-border"
                  />
                  <label htmlFor="showLegend" className="text-sm font-medium text-foreground">
                    Show Legend
                  </label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showGrid"
                    checked={customizeSettings?.showGrid}
                    onChange={(e) => setCustomizeSettings({...customizeSettings, showGrid: e?.target?.checked})}
                    className="rounded border border-border"
                  />
                  <label htmlFor="showGrid" className="text-sm font-medium text-foreground">
                    Show Grid Lines
                  </label>
                </div>
                
                <Select
                  label="Animation Speed"
                  options={animationOptions}
                  value={customizeSettings?.animationSpeed}
                  onChange={(value) => setCustomizeSettings({...customizeSettings, animationSpeed: value})}
                />
              </div>
            </div>
          </div>
          
          <div className="p-6 border-t border-border bg-muted/20">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Changes will be applied immediately to your chart display
              </div>
              <div className="flex items-center space-x-3">
                <Button 
                  variant="outline"
                  onClick={() => {
                    // Reset to defaults
                    setCustomizeSettings({
                      showProjections: true,
                      timeRange: '6months',
                      chartType: 'area',
                      showMetrics: true,
                      colorScheme: 'default',
                      showLegend: true,
                      showGrid: true,
                      animationSpeed: 'normal'
                    });
                  }}
                >
                  Reset Defaults
                </Button>
                <Button 
                  variant="default"
                  onClick={handleCustomizeApply}
                  iconName="Check"
                  iconPosition="left"
                >
                  Apply Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Download Modal
  const renderDownloadModal = () => {
    if (!showDownloadModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-md">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="Download" size={20} className="mr-2" />
                Export Live Cash Flow Analysis
              </h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowDownloadModal(false)}
                disabled={downloadLoading}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          </div>
          
          <div className="p-6">
            <p className="text-muted-foreground mb-6">
              Export comprehensive cash flow analysis with live data from your Supabase database, including trends, AI projections, and detailed metrics.
            </p>
            
            <div className="space-y-4">
              <Button
                variant="default"
                onClick={() => handleCashFlowDownload('pdf')}
                disabled={downloadLoading}
                className="w-full flex items-center justify-center space-x-2 h-12"
                iconName="FileText"
                iconPosition="left"
              >
                {downloadLoading ? 'Generating PDF...' : 'Download PDF Report'}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => handleCashFlowDownload('xls')}
                disabled={downloadLoading}
                className="w-full flex items-center justify-center space-x-2 h-12"
                iconName="Sheet"
                iconPosition="left"
              >
                {downloadLoading ? 'Generating Excel...' : 'Download Excel Report'}
              </Button>
            </div>
            
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-1">Live cash flow report includes:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Real-time cash inflow and outflow data</li>
                    <li>AI-powered 3-month projections</li>
                    <li>Monthly trends and performance metrics</li>
                    <li>Data from payments, bills, and journal entries</li>
                    <li>Bank and cash account transaction analysis</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-professional">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Cash Flow Analysis</h2>
          <p className="text-sm text-muted-foreground mt-1">Loading live data...</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center h-80">
            <div className="flex items-center space-x-3">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="text-lg text-muted-foreground">Analyzing cash flow data...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-professional">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Cash Flow Analysis</h2>
          <p className="text-sm text-muted-foreground mt-1">Error loading data</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center h-80">
            <div className="text-center">
              <Icon name="AlertCircle" size={24} className="text-error mx-auto mb-4" />
              <p className="text-error font-medium mb-2">{error}</p>
              <Button onClick={loadCashFlowData} variant="outline">
                <Icon name="RefreshCw" size={16} />
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card border border-border rounded-lg shadow-professional">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Live Cash Flow Analysis</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Real-time data with AI-powered {projectionData?.length}-month projections
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowCustomizeModal(true)}
              >
                <Icon name="Settings" size={16} />
                Customize
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowDownloadModal(true)}
              >
                <Icon name="Download" size={16} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={loadCashFlowData}
                disabled={loading}
              >
                <Icon name="RefreshCw" size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </div>
        <div className="p-6">
          {customizeSettings?.showMetrics && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-success/5 rounded-lg border border-success/20">
                <Icon name="TrendingUp" size={20} className="text-success mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Total Net Flow</p>
                <p className="text-xl font-semibold text-success">
                  ${totalNetFlow?.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {actualData?.length > 0 ? `Last ${actualData?.length} months` : 'No data available'}
                </p>
              </div>
              <div className="text-center p-4 bg-primary/5 rounded-lg border border-primary/20">
                <Icon name="BarChart3" size={20} className="text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Avg Monthly</p>
                <p className="text-xl font-semibold text-primary">
                  ${Math.round(avgMonthlyFlow)?.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {actualData?.length > 0 ? 'Live calculation' : 'No data to calculate'}
                </p>
              </div>
              <div className="text-center p-4 bg-warning/5 rounded-lg border border-warning/20">
                <Icon name="Target" size={20} className="text-warning mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Next Month</p>
                <p className="text-xl font-semibold text-warning">
                  {projectionData?.length > 0 ? `$${projectionData?.[0]?.net?.toLocaleString()}` : 'No projection'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {projectionData?.length > 0 ? 'AI projection' : 'Need 3+ months data'}
                </p>
              </div>
            </div>
          )}

          <div className="h-80 w-full">
            {allData?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={allData}>
                  <defs>
                    <linearGradient id="inflowGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--color-success)" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="var(--color-success)" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="outflowGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--color-error)" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="var(--color-error)" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  {customizeSettings?.showGrid && (
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  )}
                  <XAxis 
                    dataKey="month" 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="inflow"
                    stackId="1"
                    stroke="var(--color-success)"
                    strokeWidth={2}
                    fill="url(#inflowGradient)"
                  />
                  <Area
                    type="monotone"
                    dataKey="outflow"
                    stackId="2"
                    stroke="var(--color-error)"
                    strokeWidth={2}
                    fill="url(#outflowGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Cash Flow Data Available</h3>
                  <p className="text-sm text-muted-foreground mb-4 max-w-md">
                    Cash flow analysis requires payment, bill, or journal entry data. 
                    Start by creating invoices, recording payments, or adding journal entries to see your cash flow trends.
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">To generate cash flow data:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Create Invoices</span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Record Payments</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Add Bills</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">Journal Entries</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {customizeSettings?.showLegend && (
            <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-border">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-success rounded-full" />
                <span className="text-sm text-muted-foreground">Cash Inflow</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-error rounded-full" />
                <span className="text-sm text-muted-foreground">Cash Outflow</span>
              </div>
              {customizeSettings?.showProjections && projectionData?.length > 0 && (
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-warning rounded-full" />
                  <span className="text-sm text-muted-foreground">AI Projection</span>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full" />
                <span className="text-sm text-muted-foreground">
                  {allData?.length > 0 ? 'Live Data' : 'No Data'}
                </span>
              </div>
            </div>
          )}

          {/* Data source indicator */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <Icon name="Database" size={12} />
                <span>
                  {allData?.length > 0 
                    ? 'Real-time data from Supabase • Payments, Bills & Journal Entries' :'Connected to Supabase • Waiting for transaction data'
                  }
                </span>
                <span>•</span>
                <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customize Modal */}
      {renderCustomizeModal()}

      {/* Download Modal */}
      {renderDownloadModal()}
    </>
  );
};

export default CashFlowChart;